import { MantineProvider } from '@mantine/core';
import { DatePicker as MantineDatePicker, type DatePickerProps as MantineDatePickerProps } from '@mantine/dates';

import { Button } from '../button';

import { presets, toDateFilterBounds, toMantineRangeValue, type DateRangeValue, getDefaultCalendarDate } from './utils';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';

import './date-picker.scss';

const mantineStyles: MantineDatePickerProps<'range'>['styles'] = {
  levelsGroup: { width: '100%', display: 'block' },
  month: { width: '100%' },
  calendarHeader: { width: '100%', maxWidth: '100%' },
  calendarHeaderLevel: { width: '100%', justifyContent: 'center' },
};

export const DatePicker = ({ value, onChange, ...rest }: DatePickerProps) => {
  const handlePresetClick = (preset: (typeof presets)[number]) => {
    onChange({
      preset: preset.value,
      range: preset.getRange(),
    });
  };

  const handleRangeChange = (range: MantineRangeValue) => {
    onChange({
      preset: null,
      range: toDateFilterBounds(range),
    });
  };

  return (
    <MantineProvider forceColorScheme="light">
      <div className="date-picker-holder">
        <MantineDatePicker
          {...rest}
          type="range"
          size="lg"
          hideOutsideDates={false}
          maxDate={new Date()}
          defaultDate={getDefaultCalendarDate(value.range)}
          value={toMantineRangeValue(value.range)}
          onChange={handleRangeChange}
          styles={mantineStyles}
        />

        <div className="date-picker-presets">
          {presets.map((preset) => (
            <Button
              key={preset.value}
              size="small"
              color="grey"
              appearance="greyscale"
              data-testid={`date-picker-preset-${preset.value}`}
              active={preset.value === value.preset}
              onClick={() => handlePresetClick(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>
    </MantineProvider>
  );
};

type MantineRangeValue = Parameters<NonNullable<MantineDatePickerProps<'range'>['onChange']>>[0];

interface DatePickerProps extends Omit<MantineDatePickerProps<'range'>, 'type' | 'value' | 'onChange'> {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
}
